"use client";

import { useEffect } from "react";

export default function DebugOverlay() {
  useEffect(() => {
    // Only run in development or preview environments
    if (
      process.env.NODE_ENV !== "development" &&
      process.env.NEXT_PUBLIC_PAGES_BRANCH !== "preview"
    ) {
      return;
    }

    // Check if editMode=true is in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const editMode = urlParams.get("editMode");

    if (editMode !== "true") {
      console.log("Debug overlay disabled - editMode=true not found in URL");
      return;
    }

    console.log("Debug overlay script is running");

    function initializeDebugOverlay() {
      console.log("DOM loaded");

      const elements = document.querySelectorAll("[data-tab-id]");
      console.log("Found elements:", elements.length);

      if (elements.length === 0) {
        console.log("No elements with data-tab-id found");
        return;
      }

      let overlays: HTMLElement[] = [];

      function createOverlay(targetElement: Element) {
        console.log("Creating overlay for:", targetElement);

        const rect = targetElement.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft =
          window.pageXOffset || document.documentElement.scrollLeft;

        console.log("Element rect:", rect);

        const overlay = document.createElement("div");
        overlay.style.position = "absolute";
        overlay.style.top = rect.top + scrollTop + "px";
        overlay.style.left = rect.left + scrollLeft + "px";
        overlay.style.width = rect.width + "px";
        overlay.style.height = rect.height + "px";
        overlay.style.border = "1px dotted blue";
        overlay.style.pointerEvents = "none";
        overlay.style.zIndex = "999999";
        overlay.style.boxSizing = "border-box";

        document.body.appendChild(overlay);
        overlays.push(overlay);

        console.log("Overlay created and added to body");
        return overlay;
      }

      // Function to get clean HTML without debug attributes
      function getCleanHTML(element: Element): string {
        // Clone the element to avoid modifying the original
        const clonedElement = element.cloneNode(true) as Element;

        // Remove debug attributes from the cloned element and all its descendants
        function removeDebugAttrs(el: Element) {
          // Remove debug attributes
          el.removeAttribute("data-tab-id");
          el.removeAttribute("data-tab-name");
          el.removeAttribute("data-component-path");
          el.removeAttribute("data-component-line");
          el.removeAttribute("data-component-file");
          el.removeAttribute("data-component-name");

          // Recursively remove from children
          Array.from(el.children).forEach((child) => {
            removeDebugAttrs(child);
          });
        }

        removeDebugAttrs(clonedElement);
        return clonedElement.outerHTML;
      }

      // Function to send message immediately
      function sendMessage(targetElement: Element) {
        console.log("Sending message for:", targetElement);
        const cleanHTML = getCleanHTML(targetElement);

        const message = {
          type: "DEBUG_OVERLAY_SUBMIT",
          data: {
            componentName: targetElement.getAttribute("data-component-name"),
            componentFile: targetElement.getAttribute("data-component-file"),
            componentLine: targetElement.getAttribute("data-component-line"),
            componentPath: targetElement.getAttribute("data-component-path"),
            tabId: targetElement.getAttribute("data-tab-id"),
            renderedHTML: cleanHTML,
            timestamp: new Date().toISOString(),
          },
        };

        // Send to parent window if in iframe
        if (window.parent !== window) {
          window.parent.postMessage(message, "*");
          console.log("Message sent to parent:", message);
        } else {
          console.log("Debug overlay message (not in iframe):", message);
        }
      }

      function removeAllOverlays() {
        console.log("Removing overlays:", overlays.length);
        overlays.forEach(function (overlay) {
          if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
        });
        overlays = [];
      }

      elements.forEach(function (element) {
        console.log("Attaching listeners to:", element);

        element.addEventListener("mouseenter", function () {
          console.log("Mouse entered element");
          createOverlay(element);
        });

        element.addEventListener("mouseleave", function () {
          console.log("Mouse left element");
          removeAllOverlays();
        });

        element.addEventListener("click", function (e) {
          console.log("Element clicked - sending data immediately");
          e.preventDefault();
          e.stopPropagation();

          // Remove hover overlays
          removeAllOverlays();

          // Send message immediately
          sendMessage(element);
        });
      });

      console.log("All listeners attached");
    }

    // Initialize immediately if DOM is already loaded, otherwise wait for DOMContentLoaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initializeDebugOverlay);
    } else {
      initializeDebugOverlay();
    }

    // Cleanup function
    return () => {
      // Remove any existing overlays when component unmounts
      const overlays = document.querySelectorAll(
        '[style*="border: 3px solid blue"]'
      );
      overlays.forEach((overlay) => overlay.remove());
    };
  }, []);

  // This component doesn't render anything visible
  return null;
}
